import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, getAuthToken, setAuthToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Clock, AlertTriangle, LogOut, CreditCard, XCircle, CheckCircle2,
} from "lucide-react";

export default function Billing() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activating, setActivating] = useState(false);
  const [paypalRef, setPaypalRef] = useState("");

  const fetchStatus = () =>
    api.get("/billing/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus(null));

  useEffect(() => {
    if (!getAuthToken()) {
      nav("/saas-login", { replace: true });
      return;
    }
    const activated = params.get("activated");
    const cancelled = params.get("cancelled");
    const subscriptionId = params.get("subscription_id");
    if (activated) {
      // PayPal redirected back. Confirm activation on backend.
      api.get(`/billing/return${subscriptionId ? `?subscription_id=${subscriptionId}` : ""}`)
        .then(({ data }) => {
          if (data.status === "active") {
            toast.success("Subscription activated — welcome to KioskForce ERP!");
          } else {
            toast.info(`Subscription status: ${data.paypal_status || data.status}`);
          }
        })
        .catch(() => toast.error("Could not confirm subscription"))
        .finally(() => fetchStatus().finally(() => setLoading(false)));
    } else if (cancelled) {
      toast.info("Subscription not completed");
      fetchStatus().finally(() => setLoading(false));
    } else {
      fetchStatus().finally(() => setLoading(false));
    }
  }, [params, nav]);

  const logout = () => { setAuthToken(null); nav("/saas-login", { replace: true }); };

  const subscribe = async () => {
    // Legacy REST flow disabled. We now use the hosted PayPal form below,
    // followed by a manual activation hook. Keep the function as a no-op
    // so existing references don't crash.
    return;
  };

  const manualActivate = async () => {
    if (!window.confirm("Confirm you have completed the PayPal subscription payment. We will activate your account now.")) return;
    setActivating(true);
    try {
      await api.post("/billing/manual-activate", { paypal_reference: paypalRef.trim() || null });
      toast.success("Subscription activated — welcome to KioskForce ERP!");
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Activation failed");
    } finally {
      setActivating(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Cancel your subscription? You will keep access until the current billing period ends.")) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel", { reason: "User requested cancellation" });
      toast.success("Subscription canceled — access continues until period end");
      await fetchStatus();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Cancel failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-slate-300">Loading…</div>;
  }

  const s = status || {};
  const expired = (s.subscription_status === "trial" && (s.trial_days_left ?? 0) <= 0) || !s.billing_allows_access;
  const isActive = s.subscription_status === "active";
  const isPending = s.subscription_status === "pending";
  const isCanceled = s.subscription_status === "canceled";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6 sm:p-10" data-testid="billing-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-white text-slate-900 flex items-center justify-center">
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Subscription · Συνδρομή</h1>
              <p className="text-slate-400 text-sm">{s.company_name || "Your KioskForce tenant"}</p>
            </div>
          </div>
          <Button variant="outline" onClick={logout} className="gap-2 text-slate-900" data-testid="billing-logout">
            <LogOut className="h-4 w-4" />
            <span>Sign Out</span>
          </Button>
        </div>

        {/* Status banner */}
        <Card
          className={`p-5 mb-5 ${
            isActive
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : expired
              ? "bg-red-50 border-red-200 text-red-900"
              : isPending
              ? "bg-sky-50 border-sky-200 text-sky-900"
              : "bg-amber-50 border-amber-200 text-amber-900"
          }`}
          data-testid="billing-status-card"
        >
          <div className="flex items-center gap-3">
            {isActive ? <CheckCircle2 className="h-6 w-6" />
              : expired ? <AlertTriangle className="h-6 w-6" />
              : <Clock className="h-6 w-6" />}
            <div className="flex-1">
              <div className="font-bold uppercase tracking-wide text-sm" data-testid="billing-headline">
                {isActive
                  ? "Subscription Active · Ενεργή Συνδρομή"
                  : expired
                  ? "Trial Expired · Η δοκιμή έληξε"
                  : isPending
                  ? "Awaiting PayPal Approval · Σε αναμονή"
                  : isCanceled
                  ? "Subscription Canceled · Ακυρώθηκε"
                  : `Trial · ${s.trial_days_left ?? 0} days left · Υπολείπονται ${s.trial_days_left ?? 0} ημέρες`}
              </div>
              <div className="text-xs mt-1 opacity-80">
                Plan: <span className="font-mono font-bold">{s.subscription_plan}</span>
                {s.paypal_subscription_id && (
                  <>{" · "}<span className="font-mono">{s.paypal_subscription_id}</span></>
                )}
              </div>
            </div>
            <Badge className="bg-white/80 text-slate-800 border border-white">
              {s.subscription_status?.toUpperCase()}
            </Badge>
          </div>
        </Card>

        {/* Plan card */}
        <Card className="p-6 bg-white text-slate-900" data-testid="billing-plan-card">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-[11px] font-bold uppercase tracking-wider mb-3" data-testid="billing-trial-badge">
            🎁 2 Μήνες Εντελώς ΔΩΡΕΑΝ Δοκιμή · 2 Months Free Trial
          </div>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-5xl font-bold">€8</span>
            <span className="text-slate-500 text-sm" data-testid="billing-price-line">/ month after your 2-month trial · Cancel anytime</span>
          </div>
          <p className="text-xs text-slate-500 mb-5">
            Μηνιαία Συνδρομή · Ακύρωση οποτεδήποτε · Auto-renew via PayPal
          </p>
          <ul className="space-y-2 text-sm mb-6">
            <li className="flex items-start gap-2"><span className="text-emerald-600 font-bold">✓</span><span>Unlimited kiosks · custom names · multi-employee shifts</span></li>
            <li className="flex items-start gap-2"><span className="text-emerald-600 font-bold">✓</span><span>AI Camera OCR for invoices, cheques, receipts (GPT-5.2)</span></li>
            <li className="flex items-start gap-2"><span className="text-emerald-600 font-bold">✓</span><span>Cyprus TFA Form 4 + Corporate Tax estimator + Owner PDF export</span></li>
            <li className="flex items-start gap-2"><span className="text-emerald-600 font-bold">✓</span><span>Master Cheque Calendar · OpEx Tracker · Live Camera feeds · Wolt webhook</span></li>
            <li className="flex items-start gap-2"><span className="text-emerald-600 font-bold">✓</span><span>Daily database backups · USB export · Bilingual EN/EL</span></li>
          </ul>

          {/* Subscribe (hosted PayPal form) / cancel buttons */}
          {!isActive && (
            <div className="space-y-4" data-testid="paypal-hosted-form-wrap">
              <form
                action="https://www.paypal.com/cgi-bin/webscr"
                method="post"
                target="_top"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  margin: "20px 0",
                }}
                data-testid="paypal-hosted-form"
              >
                <input type="hidden" name="cmd" value="_s-xclick" />
                <input type="hidden" name="hosted_button_id" value="82PVJ439WEH9A" />
                <table style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
                  <tbody>
                    <tr>
                      <td style={{ fontWeight: "bold", color: "#333", paddingBottom: "5px" }}>
                        <input type="hidden" name="on0" value="Καλωσήρθες στο KioskForce Erp" />
                        Καλωσήρθες στο KioskForce Erp
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <select
                          name="os0"
                          style={{
                            padding: "8px",
                            borderRadius: "5px",
                            border: "1px solid #ccc",
                            width: "90%",
                            maxWidth: "400px",
                            backgroundColor: "#f9f9f9",
                            fontSize: "14px",
                          }}
                          defaultValue="Κουράστηκες να μετράς μετρητά στο End Shift και να ψάχνεις!!"
                        >
                          <option value="Κουράστηκες να μετράς μετρητά στο End Shift και να ψάχνεις!!">
                            Κουράστηκες να μετράς μετρητά στο End Shift και να ψάχνεις!!
                          </option>
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <input type="hidden" name="currency_code" value="EUR" />
                <input
                  type="image"
                  src="https://www.paypalobjects.com/en_US/i/btn/btn_subscribe_SM.gif"
                  border="0"
                  name="submit"
                  title="PayPal - The safer, easier way to pay online!"
                  alt="Subscribe"
                  style={{ cursor: "pointer", transform: "scale(1.2)", marginTop: "10px" }}
                  data-testid="paypal-hosted-submit"
                />
              </form>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="manual-activate-block">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                  Step 2 · Already paid? Activate now · Πληρώθηκε; Ενεργοποίηση
                </div>
                <p className="text-xs text-slate-600 mb-2">
                  Once PayPal confirms your €8/month subscription, click
                  <span className="font-bold"> Activate</span> to unlock your Portal immediately.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <input
                    type="text"
                    value={paypalRef}
                    onChange={(e) => setPaypalRef(e.target.value)}
                    placeholder="PayPal transaction ID (optional)"
                    className="flex-1 min-w-[200px] h-10 px-3 rounded-md border border-slate-300 font-mono text-xs"
                    data-testid="manual-activate-ref"
                  />
                  <Button
                    onClick={manualActivate}
                    disabled={activating}
                    className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2"
                    data-testid="manual-activate-btn"
                  >
                    <CreditCard className="h-4 w-4" />
                    {activating ? "Activating…" : "I have paid · Activate"}
                  </Button>
                </div>
                <p className="text-[10px] text-amber-700 mt-2">
                  ⚠ Activation trusts the tenant. PayPal webhook overrides this when configured.
                </p>
              </div>
            </div>
          )}

          {isActive && (
            <div className="space-y-3">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-emerald-900 text-sm">
                Your subscription is active. Renews automatically every month.
              </div>
              <Button
                onClick={cancelSubscription}
                disabled={busy}
                variant="outline"
                className="w-full h-11 border-red-300 text-red-700 hover:bg-red-50 font-bold gap-2"
                data-testid="billing-cancel"
              >
                <XCircle className="h-4 w-4" />
                {busy ? "Cancelling…" : "Cancel Subscription · Ακύρωση"}
              </Button>
              <p className="text-[10px] text-slate-500 text-center">
                Access continues until the end of the current paid period.
              </p>
            </div>
          )}

          {!s.paypal_configured && (
            <p className="text-[10px] text-amber-700 mt-4 text-center font-mono">
              ⚠ Hosted button mode · API mode also disabled ({s.paypal_env}). Webhook IPN optional.
            </p>
          )}

          <p className="text-[11px] text-slate-500 mt-6 text-center">
            Trial-expired tenants can still view historical data but Portal exports & new entries are locked.
          </p>
        </Card>
      </div>
    </div>
  );
}
